/**
 * WCAG contrast + the email scheme resolver — the builder's preview half of
 * `Services/EmailTheme.cs`. Both run `emailTheme.vectors.json`, so what the preview draws is
 * what the inbox gets (email handoff §4).
 *
 * A scheme is { primary, accent, canvas } (#RRGGBB). Null is PORTAL.
 */

export const BLACK = '#0A0A0A';
export const WHITE = '#FFFFFF';

/** The built-in PORTAL scheme, resolved. */
export const PORTAL_THEME = Object.freeze({
  canvas: '#F2F2F2', canvasInk: BLACK, canvasMuted: '#6B6B6B',
  primary: BLACK, primaryInk: WHITE, primarySoft: '#D4D4D4',
  accent: '#FD199F', label: '#FF33C6', link: BLACK,
});

export const isHex = (v) => /^#[0-9a-f]{6}$/i.test(v || '');

/** '#abc123' / 'abc123' → '#ABC123'; anything else → null. */
export function normalizeHex(v) {
  let t = String(v || '').trim();
  if (t.length === 6 && !t.startsWith('#')) t = `#${t}`;
  return isHex(t) ? t.toUpperCase() : null;
}

export function luminance(hex) {
  let h = String(hex || '#000000').replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  h = h.padEnd(6, '0').slice(0, 6);
  const c = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrast(a, b) {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** Black or white, whichever reads better on `bg`. Ties go to black. */
export const inkOn = (bg) => (contrast(bg, BLACK) >= contrast(bg, WHITE) ? BLACK : WHITE);

/** A scheme → the template's flat params. Null / incomplete → PORTAL. */
export function resolveScheme(scheme) {
  if (!scheme || !isHex(scheme.primary) || !isHex(scheme.accent) || !isHex(scheme.canvas)) return PORTAL_THEME;
  const p = scheme.primary.toUpperCase();
  const a = scheme.accent.toUpperCase();
  const c = scheme.canvas.toUpperCase();
  const primaryInk = inkOn(p);
  const light = primaryInk === WHITE;
  const canvasInk = inkOn(c);
  return {
    canvas: c, canvasInk, canvasMuted: canvasInk === WHITE ? '#A3A3A3' : '#6B6B6B',
    primary: p, primaryInk, primarySoft: light ? '#D4D4D4' : '#333333',
    accent: a, label: contrast(a, p) >= 3 ? a : (light ? '#D4D4D4' : '#3A3A3A'),
    link: contrast(p, WHITE) >= 4.5 ? p : BLACK,
  };
}

/** A band's background for its tone. */
export const bandBg = (tone, t) => (tone === 'accent' ? t.accent : tone === 'black' ? BLACK : t.primary);

/** A band's text colour: the chosen ink, or black / white by contrast when auto. */
export function bandFg(ink, bg, t) {
  switch (ink) {
    case 'white': return WHITE;
    case 'black': return BLACK;
    case 'accent': return t.accent;
    case 'primary': return t.primary;
    default: return inkOn(bg);
  }
}

/** The two scheme warnings (not blocking). */
export function schemeWarning({ primary, accent, canvas }) {
  if (isHex(primary) && isHex(accent) && contrast(primary, accent) < 1.6) return 'Primary and accent are too close. The ring around the label will disappear.';
  if (isHex(canvas) && isHex(primary) && contrast(canvas, primary) < 1.4) return 'Background and primary are too close. The top card will not stand out.';
  return null;
}
